/**
 * asp-pincode.js
 * ASP pincode delivery — PDP middleware + cart-level checker.
 *
 * PDP submit flow:
 *   1. Call ASP API  { pincode, sku, quantity }
 *   2a. status:true   → show API message  ✓
 *   2b. status:false  → if express enabled  → PincodeExpress fallback
 *                     → if express disabled → show API message as standard
 *   3.  API error     → same 2b path
 *
 * Cart-level: window.AspPincodeCart (see bottom of file)
 *   - onCartOpen()                          → Promise.all check for every item
 *   - onCartItemUpdate(sku, varId, qty)     → check that one item
 *   - checkItem(sku, varId, qty)            → returns { message, source } or null
 *
 * Loaded only when settings.enable_asp_pincode is true (layout/theme.liquid).
 */

// ─── Query helpers (same signature as estimate-delivery.js) ─────────────────
window.qs =
  window.qs ||
  function (sel, ctx) {
    return (ctx || document).querySelector(sel);
  };
window.qsa =
  window.qsa ||
  function (sel, ctx) {
    return (ctx || document).querySelectorAll(sel);
  };

// ─── PDP Class ───────────────────────────────────────────────────────────────

class AspPincode {
  constructor(config) {
    if (!config || !config.enabled) return;
    if (!config.apiUrl) {
      console.warn(
        "[AspPincode] asp_pincode_api_url is not set in theme settings.",
      );
      return;
    }
    this.config = config;
    this.variantId = config.variantId || null;
    this._init();
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  _init() {
    this.section = qs("#pdp-pincode-section");
    if (!this.section) return;

    this.input = qs(".pincode-input-pdp", this.section);
    this.submitBtn = qs(".pin-submit-pdp", this.section);
    this.errorEl = qs(".pincode-error-pdp", this.section);
    this.expressMsg = qs(".express-delivery-msg-pdp", this.section);
    this.soldByMsg = qs(".asp-sold-by-pdp-container", this.section);
    this.standardMsg = qs(".standard-delivery-msg-pdp", this.section);
    this.skuInput = qs("#pdp-asp-sku");
    this.variantInput = qs("#pdp-asp-variant-id");

    // Show section — overrides .pdp-pincode { display:none } in SCSS
    this.section.classList.add("asp-active");

    // Ensure messages start hidden via inline style — matches how Express hides them.
    // HTML already has style="display:none" on these, but normalise via jQuery
    // so both systems use the same mechanism and there is no class vs inline conflict.
    if (window.jQuery) {
      const els = [this.errorEl, this.expressMsg, this.standardMsg].filter(
        Boolean,
      );
      window.jQuery(els).hide();
    }

    // Block Express from flashing its message before our API resolves.
    // asp-loading CSS suppresses .output-pdp with !important until _unblockMessages() runs.
    this.section.classList.add("asp-loading");
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = "...";
    }

    // Replace the jQuery express click handler with ours.
    // custom-all.js is deferred before asp-pincode.js, so the express handler
    // is already registered; jQuery.off() strips it cleanly.
    // Use stopImmediatePropagation to block the express handler.
    // Reason: custom-all.js registers its express click handler via $(document).ready()
    // which fires AFTER deferred scripts run — so jQuery.off('click') in _init() removes it,
    // but the express handler is re-added moments later. Since our handler is registered first
    // it fires first; stopImmediatePropagation() prevents the express handler from ever running.
    if (window.jQuery) {
      window
        .jQuery(this.submitBtn)
        .off("click")
        .on("click", (e) => {
          e.stopImmediatePropagation();
          this._handleSubmit();
        });
    } else {
      this.submitBtn?.addEventListener(
        "click",
        (e) => {
          e.stopImmediatePropagation();
          this._handleSubmit();
        },
        true,
      );
    }

    this.input?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this._handleSubmit();
      }
    });

    // Digits only
    this.input?.addEventListener("input", () => {
      this.input.value = this.input.value.replace(/\D/g, "");
    });

    const activeVariantEl = document.querySelector(".variant-items-container .variant-item.current-variant");
    if (activeVariantEl) {
      const activeVariantId = activeVariantEl.getAttribute("variant-id");
      if (activeVariantId) {
        this.variantId = activeVariantId;
        if (this.variantInput) this.variantInput.value = activeVariantId;
        if (window.meta?.product?.variants) {
          const variant = window.meta.product.variants.find((v) => v.id == activeVariantId);
          if (variant) {
            this.config.sku = variant.sku;
            if (this.skuInput) this.skuInput.value = variant.sku;
          }
        }
      }
    }

    this._bindVariantChange();
    this._restoreAndAutoCheck();
    // Detect PDP qty change by patching the .product-qty input's value setter.
    // script.js's qty handler calls e.stopImmediatePropagation() which kills
    // every event listener (capture, bubble, direct) — nothing can receive the
    // click event. Patching the value property setter bypasses events entirely:
    // when script.js does `input.value = q` our setter fires unconditionally.
    const qtyInput = document.querySelector(".product-qty");
    if (qtyInput) {
      const self = this; // capture AspPincode instance — 'this' inside set() is the element
      const desc = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value",
      );
      Object.defineProperty(qtyInput, "value", {
        configurable: true,
        get() {
          return desc.get.call(this);
        },
        set(v) {
          const prev = desc.get.call(this);
          desc.set.call(this, v); // set value first so script.js continues normally
          if (String(v) !== String(prev) && self._getSavedPincode()) {
            qs(".pin-submit-pdp")?.click();
          }
        },
      });
    }
  }

  // ─── Variant change ───────────────────────────────────────────────────────

  _bindVariantChange() {
    document.addEventListener("click", (e) => {
      const item = e.target.closest(".variant-item");
      if (!item) return;

      const variantId = item.getAttribute("variant-id");
      if (!variantId) return;

      this.variantId = variantId;
      if (this.variantInput) this.variantInput.value = variantId;

      if (window.meta?.product?.variants) {
        const variant = window.meta.product.variants.find(
          (v) => v.id == variantId,
        );
        if (variant) {
          this.config.sku = variant.sku;
          if (this.skuInput) this.skuInput.value = variant.sku;
        }
      }

      // Re-check delivery when variant changes (if pincode already saved)
      if (this._getSavedPincode()) this._handleSubmit();
    });
  }

  // ─── Pincode restore priority ─────────────────────────────────────────────
  //   1. user_pincode cookie  (set by us or express system)
  //   2. existing_pincode cookie  (set by estimate-delivery.js)
  //   3. .asp-last-order-zip hidden input  (Liquid → customer last order)
  //   4. Geolocation  (nominatim reverse geocode)

  _getSavedPincode() {
    return (
      this._getCookie("user_pincode") ||
      this._getCookie("existing_pincode") ||
      null
    );
  }

  _restoreAndAutoCheck() {
    const saved = this._getSavedPincode();

    if (saved) {
      this.input.value = saved;
      this.submitBtn.textContent = "Update";
      this._handleSubmit();
      return;
    }

    const lastOrderZip = qs(".asp-last-order-zip")?.value?.trim();
    if (lastOrderZip) {
      this.input.value = lastOrderZip;
      this._setCookie("user_pincode", lastOrderZip, 30);
      this._handleSubmit();
      return;
    }

    this._tryGeolocation();
  }

  _tryGeolocation() {
    if (!navigator.geolocation) {
      // No geolocation support — nothing to auto-check, release the block
      this._unblockMessages();
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = "Check";
      }
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`,
          );
          const loc = await res.json();
          const pincode = (loc?.address?.postcode || "").replace(/\D/g, "");
          if (pincode.length === 6) {
            this.input.value = pincode;
            this._handleSubmit(); // _unblockMessages called in its finally
          } else {
            // Postcode not usable — release
            this._unblockMessages();
            if (this.submitBtn) {
              this.submitBtn.disabled = false;
              this.submitBtn.textContent = "Check";
            }
          }
        } catch (err) {
          console.error("[AspPincode] Geolocation lookup failed:", err);
          this._unblockMessages();
          if (this.submitBtn) {
            this.submitBtn.disabled = false;
            this.submitBtn.textContent = "Check";
          }
        }
      },
      (err) => {
        // User denied or unavailable — release the block
        console.warn("[AspPincode] Geolocation unavailable:", err.message);
        this._unblockMessages();
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = "Check";
        }
      },
    );
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  async _handleSubmit() {
    if (this._isHandling) return;
    this._isHandling = true;

    // Apply loading state immediately before any await so the browser can paint '...'
    this._clearMessages();
    this.section.classList.add("asp-loading");
    this._setLoading(true);

    // Double-rAF: guarantees the '...' state is painted whether called from a
    // user click or from a setTimeout (single-rAF isn't enough in the latter case).
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    );

    // Wrap everything in a single try/finally so _isHandling is ALWAYS reset
    // on every exit path — early returns, validation failure, and exceptions.
    try {
      const pincode = this.input?.value.trim() || "";

      if (!/^[1-9][0-9]{5}$/.test(pincode)) {
        this._showError();
        return; // finally resets _isHandling, unblocks, and re-enables button
      }

      const sku = this.skuInput?.value || this.config.sku || "";
      const variantId =
        this.variantInput?.value ||
        this.variantId ||
        this.config.variantId ||
        "";
      const quantity = await this._getTotalQuantity(variantId);
      const data = await this._callApi(pincode, sku, quantity);

      if (data && (data.status === true || data.status === "true")) {
        this._onAspSuccess(data, pincode);
      } else {
        this._onAspFallback(pincode, data?.message || null);
      }
    } catch (err) {
      console.warn(
        "[AspPincode] Unexpected error, falling back to express:",
        err.message,
      );
      this._onAspFallback(this.input?.value?.trim() || "", null);
    } finally {
      this._isHandling = false;
      this._unblockMessages();
      this._setLoading(false);
    }
  }

  // ─── Quantity (PDP qty + existing cart qty for this variant) ──────────────

  async _getTotalQuantity(variantId) {
    const pdpQty = parseInt(qs(".product-qty")?.value || "1", 10) || 1;
    if (!variantId) return pdpQty;

    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      const item = cart.items.find(
        (i) => String(i.variant_id) === String(variantId),
      );
      const cartQty = item ? item.quantity : 0;
      return pdpQty + cartQty;
    } catch {
      return pdpQty;
    }
  }

  // ─── API call ─────────────────────────────────────────────────────────────

  async _callApi(pincode, sku, quantity) {
    const headers = { "Content-Type": "application/json" };
    if (this.config.authToken)
      headers["Authorization"] = "Basic " + this.config.authToken;

    const shop = (window.Shopify && window.Shopify.shop) || "";

    const url =
      (this.config.apiUrl || "") +
      "/storefront/asp/check-supplier" +
      (shop ? "?shop=" + shop : "");

    let response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          sku,
          quantity: String(quantity),
          pincode: parseInt(pincode, 10),
        }),
      });
    } catch (networkErr) {
      console.warn(
        "[AspPincode] Network/CORS error — check API URL and CORS config:",
        networkErr.message,
      );
      return null;
    }

    if (!response || !response.ok) {
      console.warn(
        "[AspPincode] API returned",
        response ? response.status : "no response",
      );
      return null;
    }

    try {
      return await response.json();
    } catch (parseErr) {
      console.warn(
        "[AspPincode] JSON parse error in API response:",
        parseErr.message,
      );
      return null;
    }
  }

  // ─── ASP success ──────────────────────────────────────────────────────────

  _onAspSuccess(data, pincode) {
    this._setCookie("user_pincode", pincode, 30);

    const isExpress =
      typeof window.PincodeExpress?.isExpressPincode === "function"
        ? window.PincodeExpress.isExpressPincode(pincode)
        : false;
    const sapCode =
      typeof window.PincodeExpress?.getSapCodeForPincode === "function"
        ? window.PincodeExpress.getSapCodeForPincode(pincode)
        : "";
    this._setCookie("isExpress", String(isExpress), 30);
    if (sapCode) this._setCookie("sapCode", sapCode, 30);

    const supplierCode = (data.data && data.data.supplier_code) || "";
    if (supplierCode) this._setCookie("asp_code", supplierCode, 30);

    this._updateFormProperties(true, supplierCode);

    this._showExpress(
      data.message ||
        window.DeliveryConfig?.getPdpDeliveryText?.() ||
        "Delivery available at your pincode",
    );
    this._updateHeaderSubtext(pincode);

    // Show Sold By supplier title if available
    const supplierName =
      (data.data && (data.data.supplier_name || data.data.supplierName)) ||
      data.supplier_name ||
      data.supplierName ||
      "";
    if (supplierName) {
      const label = this.config.soldByLabel || "Sold By:";
      const badgeHTML = `${label} <span class="asp-sold-by-badge">${supplierName}</span>`;
      if (this.soldByMsg) {
        this.soldByMsg.innerHTML = badgeHTML;
        this.soldByMsg.style.display = "block";
      }
    } else {
      if (this.soldByMsg) {
        this.soldByMsg.innerHTML = "";
        this.soldByMsg.style.display = "none";
      }
    }

    // GA4 — uncomment when event tracking is formally requested.
    // Uses existing event "pincode_entered", same params, pincode_source = 'pdp-asp'.
    // No new GTM trigger or GA4 event setup needed — existing trigger fires automatically.
    // if (typeof firePincodeGAEvent === 'function') {
    //   firePincodeGAEvent(pincode, true, 'pdp-asp');
    // }
  }

  // ─── ASP fallback ─────────────────────────────────────────────────────────

  _onAspFallback(pincode, apiMsg) {
    this._updateFormProperties(false, null);

    const expressEnabled = window.PINCODE_EXPERIMENT_CONFIG?.enabled;
    const inTestGroup = this._getCookie("pincode_ab_group") === "test";
    const expressReady =
      typeof window.PincodeExpress?.isExpressPincode === "function";

    if (expressEnabled && inTestGroup && expressReady) {
      this._runExpressCheck(pincode);
    } else {
      this._setCookie("user_pincode", pincode, 30);
      this._setCookie("isExpress", "false", 30);
      this._showStandard(
        apiMsg ||
          window.DeliveryConfig?.standardMessage?.() ||
          "Free delivery available at your pincode",
      );
      this._updateHeaderSubtext(pincode);
    }
  }

  _runExpressCheck(pincode) {
    const isExpress = window.PincodeExpress.isExpressPincode(pincode);
    const sapCode = window.PincodeExpress.getSapCodeForPincode(pincode);

    this._setCookie("user_pincode", pincode, 30);
    this._setCookie("isExpress", String(isExpress), 30);
    if (sapCode) this._setCookie("sapCode", sapCode, 30);

    this._updateHeaderSubtext(pincode);

    if (isExpress) {
      this._showExpress(
        window.DeliveryConfig?.getPdpDeliveryText?.() ||
          "Delivery within 2-3 working days",
      );
    } else {
      this._showStandard(
        window.DeliveryConfig?.standardMessage?.() ||
          "Free delivery available at your pincode",
      );
    }

    // GA4 — uncomment when event tracking is formally requested.
    // Uses existing event "pincode_entered", pincode_source = 'pdp-asp-fallback'.
    // No new GTM trigger or GA4 event setup needed.
    // if (typeof firePincodeGAEvent === 'function') {
    //   firePincodeGAEvent(pincode, isExpress, 'pdp-asp-fallback');
    // }
  }

  // ─── UI helpers ───────────────────────────────────────────────────────────

  _showExpress(text) {
    // Exact same pattern as Express: $pdpStandardMsg.hide() / $pdpExpressMsg.html(text).show()
    if (window.jQuery) {
      window.jQuery(this.standardMsg).hide();
      window.jQuery(this.expressMsg).html(text).show();
    } else {
      if (this.standardMsg) this.standardMsg.style.display = "none";
      if (this.expressMsg) {
        this.expressMsg.innerHTML = text;
        this.expressMsg.style.display = "block";
      }
    }
  }

  _showStandard(text) {
    const msg =
      text ||
      window.DeliveryConfig?.standardMessage?.() ||
      "Free delivery available at your pincode";
    // Exact same pattern as Express: $pdpExpressMsg.hide() / $pdpStandardMsg.html(msg).show()
    if (window.jQuery) {
      window.jQuery(this.expressMsg).hide();
      window.jQuery(this.standardMsg).html(msg).show();
    } else {
      if (this.expressMsg) this.expressMsg.style.display = "none";
      if (this.standardMsg) {
        this.standardMsg.innerHTML = msg;
        this.standardMsg.style.display = "block";
      }
    }
  }

  _showError() {
    if (window.jQuery) {
      window.jQuery(this.errorEl).show();
    } else {
      if (this.errorEl) this.errorEl.style.display = "block";
    }
    this.input.value = "";
  }

  _clearMessages() {
    if (window.jQuery) {
      const els = [this.errorEl, this.expressMsg, this.standardMsg].filter(
        Boolean,
      );
      window.jQuery(els).hide();
    } else {
      [this.errorEl, this.expressMsg, this.standardMsg].forEach((el) => {
        if (el) el.style.display = "none";
      });
    }
    if (this.soldByMsg) {
      this.soldByMsg.innerHTML = "";
      this.soldByMsg.style.display = "none";
    }
  }

  _unblockMessages() {
    // Remove the load-time block — allows the inline display set by _showExpress/_showStandard to take effect.
    this.section.classList.remove("asp-loading");
  }

  _setLoading(state) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = state;
    this.submitBtn.textContent = state
      ? "..."
      : this._getSavedPincode()
        ? "Update"
        : "Check";
  }

  _updateFormProperties(isAsp, supplierCode) {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach((form) => {
      let isAspInput = form.querySelector('input[name="properties[_isASP]"]');
      if (!isAspInput) {
        isAspInput = document.createElement("input");
        isAspInput.type = "hidden";
        isAspInput.name = "properties[_isASP]";
        form.appendChild(isAspInput);
      }

      let aspCodeInput = form.querySelector(
        'input[name="properties[_ASPCode]"]',
      );
      if (!aspCodeInput) {
        aspCodeInput = document.createElement("input");
        aspCodeInput.type = "hidden";
        aspCodeInput.name = "properties[_ASPCode]";
        form.appendChild(aspCodeInput);
      }

      if (isAsp && supplierCode) {
        isAspInput.value = "true";
        isAspInput.disabled = false;
        aspCodeInput.value = supplierCode;
        aspCodeInput.disabled = false;
      } else {
        isAspInput.disabled = true;
        aspCodeInput.disabled = true;
      }
    });
  }

  _updateHeaderSubtext(pincode) {
    if (!window.jQuery) return;
    const svg =
      '<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.1836 1.0542L5.61968 5.61816L1.05469 1.0542" stroke="#3B3C3D" stroke-width="2.10835" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
    window
      .jQuery(".pincode-checker .subtext, .pincode-checker-mob .subtext")
      .html("Deliver to " + pincode + " " + svg);
  }

  // ─── Cookie helpers ───────────────────────────────────────────────────────

  _getCookie(name) {
    const v = "; " + document.cookie;
    const p = v.split("; " + name + "=");
    if (p.length === 2) return p.pop().split(";").shift();
    return null;
  }

  _setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie =
      name + "=" + value + "; expires=" + d.toUTCString() + "; path=/";
  }
}

// ─── Boot PDP ────────────────────────────────────────────────────────────────

(function () {
  function boot() {
    if (window.ASP_PINCODE_CONFIG && window.ASP_PINCODE_CONFIG.enabled) {
      new AspPincode(window.ASP_PINCODE_CONFIG);
    }
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

// ─── Cart-level checker  (window.AspPincodeCart) ─────────────────────────────
//
//  Usage from cart drawer JS:
//
//    // On cart open — check all items at once
//    const results = await window.AspPincodeCart.onCartOpen();
//    // results: { [variantId]: { message, source } }
//
//    // On single item qty change
//    const result = await window.AspPincodeCart.onCartItemUpdate(sku, variantId, newQty);
//    // result: { message, source } | null
//
//  source values:
//    'asp'          — ASP API returned status:true
//    'express'      — fell back to express, isExpressPincode = true
//    'standard'     — fell back, not express pincode
//    'asp-standard' — ASP status:false + express disabled → used ASP message

window.AspPincodeCart = (function () {
  // ── Result cache — prevents duplicate API calls within the same cart operation ──
  // _updateCartItemQuantity calls checkItem (primes cache), then dropdowncart calls
  // checkAll → checkItem again seconds later. Cache hit = no second API call.
  const _cache = {};
  const _CACHE_TTL = 15000; // 15 seconds

  // ── Private helpers ───────────────────────────────────────────────────────

  function _getCookie(name) {
    const v = "; " + document.cookie;
    const p = v.split("; " + name + "=");
    if (p.length === 2) return p.pop().split(";").shift();
    return null;
  }

  function _getPincode() {
    return _getCookie("user_pincode") || _getCookie("existing_pincode") || null;
  }

  async function _callAsp(pincode, sku, quantity) {
    const cfg = window.ASP_PINCODE_CONFIG;
    if (!cfg || !cfg.enabled || !cfg.apiUrl) return null;

    const headers = { "Content-Type": "application/json" };
    if (cfg.authToken) headers["Authorization"] = "Basic " + cfg.authToken;

    const shop = (window.Shopify && window.Shopify.shop) || "";

    const url =
      cfg.apiUrl +
      "/storefront/asp/check-supplier" +
      (shop ? "?shop=" + shop : "");

    let res;
    try {
      res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          sku,
          quantity: String(quantity),
          pincode: parseInt(pincode, 10),
        }),
      });
    } catch (networkErr) {
      console.warn("[AspPincodeCart] Network/CORS error:", networkErr.message);
      return null;
    }

    if (!res || !res.ok) return null;

    try {
      return await res.json();
    } catch {
      return null;
    }
  }

  function _expressResult(pincode) {
    const expressEnabled = window.PINCODE_EXPERIMENT_CONFIG?.enabled;
    const expressReady =
      typeof window.PincodeExpress?.isExpressPincode === "function";

    if (!expressEnabled || !expressReady) return null;

    const isExpress = window.PincodeExpress.isExpressPincode(pincode);
    if (isExpress) {
      return {
        message:
          window.DeliveryConfig?.standardItemMessage?.() || "Free Delivery",
        source: "express",
      };
    }
    return {
      message:
        window.DeliveryConfig?.standardMessage?.() || "Free delivery available",
      source: "standard",
    };
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Check a single cart item.
   * @param {string} sku
   * @param {string|number} variantId
   * @param {number} quantity
   * @returns {Promise<{message:string, source:string}|null>}
   */
  async function checkItem(sku, variantId, quantity) {
    const pincode = _getPincode();
    if (!pincode) return null;

    // Cache key: pincode + sku + quantity — same combo = same result within TTL
    const cacheKey = pincode + ":" + sku + ":" + quantity;
    const hit = _cache[cacheKey];
    if (hit && Date.now() - hit.ts < _CACHE_TTL) {
      console.log("[AspPincodeCart] cache hit:", cacheKey);
      return hit.result;
    }

    console.log("[AspPincodeCart] API call:", cacheKey);
    const data = await _callAsp(pincode, sku, quantity);

    let result = null;
    if (data && (data.status === true || data.status === "true")) {
      result = {
        message: data.message,
        source: "asp",
        supplierCode: (data.data && data.data.supplier_code) || null,
      };
    } else {
      const expressRes = _expressResult(pincode);
      if (expressRes) {
        result = expressRes;
      } else if (data && data.message) {
        result = { message: data.message, source: "asp-standard" };
      }
    }

    _cache[cacheKey] = { result, ts: Date.now() };
    return result;
  }

  /**
   * Check all items in the provided cart items array using Promise.all.
   * @param {Array<{sku:string, variant_id:string|number, quantity:number}>} cartItems
   * @returns {Promise<Object>} map of variantId → { message, source }
   */
  /**
   * @returns {Promise<{results: Object, changed: boolean}>}
   *   results  — map of variantId → { message, source }
   *   changed  — true if any /cart/change.js property write was made
   *              (caller uses this to decide whether to re-fetch fresh cart keys)
   */
  async function checkAll(cartItems) {
    if (!cartItems || !cartItems.length) return { results: {}, changed: false };
    const pincode = _getPincode();
    if (!pincode) return { results: {}, changed: false };

    const entries = await Promise.all(
      cartItems.map(async (item) => {
        const result = await checkItem(
          item.sku,
          item.variant_id,
          item.quantity,
        );
        return [String(item.variant_id), result];
      }),
    );

    const map = {};
    entries.forEach(([vid, result]) => {
      if (result) map[vid] = result;
    });

    // Sync _isASP + _ASPCode on every cart item sequentially.
    // `changed` tracks whether any write was made so the caller can decide
    // whether to re-fetch /cart.js for fresh line item keys.
    let changed = false;

    for (const item of cartItems) {
      const result = map[String(item.variant_id)];
      const props = item.properties || {};
      const isAspConfirmed =
        result && result.source === "asp" && result.supplierCode;

      if (isAspConfirmed) {
        if (
          props["_isASP"] !== "true" ||
          props["_ASPCode"] !== result.supplierCode
        ) {
          changed = true;
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: Object.assign({}, props, {
                _isASP: "true",
                _ASPCode: result.supplierCode,
              }),
            }),
          }).catch(() => {});
        }
      } else if (props["_isASP"] === "true") {
        changed = true;
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: Object.assign({}, props, {
              _isASP: "false",
              _ASPCode: null,
            }),
          }),
        }).catch(() => {});
      }
    }

    return { results: map, changed };
  }

  /**
   * Fetch /cart.js then check every item.
   * Call this when the cart drawer opens.
   * @returns {Promise<Object>} map of variantId → { message, source }
   */
  async function onCartOpen() {
    const pincode = _getPincode();
    if (!pincode) return {};

    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      const { results } = await checkAll(cart.items);
      return results;
    } catch (err) {
      console.error("[AspPincodeCart] onCartOpen error:", err);
      return {};
    }
  }

  /**
   * Called when a single cart item quantity is updated.
   * @param {string} sku
   * @param {string|number} variantId
   * @param {number} newQuantity
   * @returns {Promise<{message:string, source:string}|null>}
   */
  async function onCartItemUpdate(sku, variantId, newQuantity) {
    return await checkItem(sku, variantId, newQuantity);
  }

  return {
    checkItem,
    checkAll,
    onCartOpen,
    onCartItemUpdate,
    getPincode: _getPincode,
  };
})();
